import React from 'react';
import { Player } from '../../types';

export interface PlayerListItemProps {
  player: Player;
  isCurrentUser: boolean;
  isCurrentTurn: boolean;
  isSelected: boolean;
  onClick: () => void;
}

/**
 * PlayerListItem Component
 * 
 * Displays a single player entry in the PlayerPanel with:
 * - Username and character name
 * - Health indicator (bar or percentage)
 * - Visual distinction for eliminated players
 * - Highlighting for current user and current turn player
 * 
 * Requirements: 2.2, 2.3, 2.4, 2.5, 2.6
 */
export const PlayerListItem: React.FC<PlayerListItemProps> = ({
  player,
  isCurrentUser,
  isCurrentTurn,
  isSelected,
  onClick,
}) => {
  const { character, stats, username } = player;
  
  // Calculate health percentage for the health bar
  const maxHealth = character?.baseStats.health ?? 100;
  const healthPercentage = Math.min(100, Math.max(0, (stats.health / maxHealth) * 100));

  // Build CSS classes for the container
  const containerClasses = [
    'player-list-item',
    'relative',
    'p-3',
    'rounded-lg',
    'cursor-pointer',
    'transition-all',
    'duration-200',
    'border',
  ];

  // Apply distinct styling for eliminated players (Requirement 2.4)
  if (stats.isEliminated) {
    containerClasses.push(
      'player-list-item--eliminated',
      'bg-gray-800/50',
      'border-gray-600',
      'opacity-60'
    );
  } else if (isSelected) {
    containerClasses.push(
      'player-list-item--selected',
      'bg-primary-500/20',
      'border-primary-400'
    );
  } else if (isCurrentTurn) {
    // Highlight current turn player (Requirement 2.6)
    containerClasses.push(
      'player-list-item--current-turn',
      'bg-green-500/20',
      'border-green-400'
    );
  } else if (isCurrentUser) {
    // Highlight current user distinctly (Requirement 2.5)
    containerClasses.push(
      'player-list-item--current-user',
      'bg-blue-500/10',
      'border-blue-400/50'
    );
  } else {
    containerClasses.push(
      'bg-gray-800/30',
      'border-gray-700',
      'hover:bg-gray-700/50',
      'hover:border-gray-600'
    );
  }

  return (
    <div
      className={containerClasses.join(' ')}
      onClick={onClick}
      role="button"
      tabIndex={0}
      data-player-id={player.userId}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-selected={isSelected}
      aria-label={`${username}${character ? `, playing as ${character.name}` : ''}${stats.isEliminated ? ', eliminated' : ''}${isCurrentTurn ? ', current turn' : ''}`}
    >
      {/* Turn indicator badge */}
      {isCurrentTurn && !stats.isEliminated && (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
      )}

      {/* Player info section */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex-1 min-w-0">
          {/* Username with badges (Requirement 2.2) */}
          <div className="flex items-center gap-2">
            <span 
              className={`font-medium truncate ${
                stats.isEliminated ? 'text-gray-400 line-through' : 'text-white'
              }`}
            >
              {username}
            </span>
            
            {/* Current user badge (Requirement 2.5) */}
            {isCurrentUser && (
              <span className="text-xs bg-blue-500/30 text-blue-300 px-1.5 py-0.5 rounded">
                You
              </span>
            )}
            
            {/* Current turn badge (Requirement 2.6) */}
            {isCurrentTurn && !stats.isEliminated && (
              <span className="text-xs bg-green-500/30 text-green-300 px-1.5 py-0.5 rounded">
                Turn
              </span>
            )}
          </div>
          
          {/* Character name (Requirement 2.2) */}
          <div 
            className={`text-sm truncate ${
              stats.isEliminated ? 'text-gray-500' : 'text-gray-400'
            }`}
          >
            {character?.name ?? 'No character'}
          </div>
        </div>

        {/* Eliminated badge (Requirement 2.4) */}
        {stats.isEliminated && (
          <span className="text-xs bg-red-500/30 text-red-400 px-2 py-1 rounded font-medium">
            OUT
          </span>
        )}
      </div>

      {/* Health indicator (Requirement 2.3) */}
      <div className="health-indicator mt-1">
        <div 
          className="relative h-6 bg-gray-900 rounded-lg overflow-hidden border border-gray-600 shadow-inner"
          role="progressbar"
          aria-valuenow={stats.health}
          aria-valuemin={0}
          aria-valuemax={maxHealth}
          aria-label={`Health: ${stats.health} of ${maxHealth}`}
        >
          {/* Background track */}
          <div className="absolute inset-0 bg-gray-800" />
          
          {/* Health bar fill with gradient */}
          <div
            className="absolute top-0 bottom-0 left-0 transition-all duration-500 ease-out"
            style={{ 
              width: `${healthPercentage}%`,
              background: stats.isEliminated 
                ? 'linear-gradient(to right, #6b7280, #9ca3af)' 
                : healthPercentage > 75 
                  ? 'linear-gradient(to right, #16a34a, #22c55e)' 
                  : healthPercentage > 50 
                    ? 'linear-gradient(to right, #ca8a04, #eab308)' 
                    : healthPercentage > 25 
                      ? 'linear-gradient(to right, #ea580c, #f97316)' 
                      : 'linear-gradient(to right, #dc2626, #ef4444)'
            }}
          >
            {/* Shine effect overlay */}
            <div 
              className="absolute inset-0" 
              style={{ background: 'linear-gradient(to bottom, rgba(255,255,255,0.25) 0%, transparent 50%)' }} 
            />
          </div>
          
          {/* Health text centered in bar */}
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <span 
              className="text-xs font-bold tracking-wide text-white"
              style={{ textShadow: '0 1px 2px rgba(0,0,0,0.8), 0 0 4px rgba(0,0,0,0.5)' }}
            >
              {stats.health}/{maxHealth}
            </span>
          </div>
          
          {/* Subtle pulse animation when low health */}
          {!stats.isEliminated && healthPercentage <= 25 && healthPercentage > 0 && (
            <div className="absolute inset-0 bg-red-500/30 animate-pulse" />
          )}
        </div>
      </div>

      {/* Knocked down indicator */}
      {stats.isKnockedDown && !stats.isEliminated && (
        <div className="mt-2 text-xs text-orange-400 flex items-center gap-1">
          <span className="w-2 h-2 bg-orange-400 rounded-full" />
          Knocked Down
        </div>
      )}
    </div>
  );
};
